"use client"

import Link from "next/link"
import type { ReactNode } from "react"
import { useCountry } from "@/components/layout/country-provider"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { track } from "@/lib/analytics"
import { isAllowedInCountry } from "@/lib/country-gates"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { cn } from "@/lib/utils"

/**
 * The result-panel CTA shared by every calculator under /tools: it hands the
 * reader to the quiz (`/quiz-focus`) rather than straight into booking.
 *
 * From 30 Sep to 22 Oct 2026 this button booked the blood check-up directly:
 * 11 of 149 readers with a result opened the booking app and none booked,
 * against 12 of 76 who opened the quiz while it was the quiz. A score is a
 * free, anonymous self-check; a few questions are a smaller step from there
 * than a paid booking, and the quiz leaves an email behind when the visit
 * ends without one.
 *
 * Gated on `page:quiz` rather than on a country: every market that serves
 * the calculators serves the quiz today, but a market added to the tools
 * first would otherwise render a button that 404s in the one panel the page
 * treats as its reward.
 *
 * The tracking location keeps its `<calculator>-calculator-result` shape so
 * the click series reads across the switch; only the label changes.
 */
export function CalculatorQuizCta({
  calculator,
  children,
}: {
  /** Short calculator id (`bmi`, `fib4`…): test id and tracking label. */
  calculator: string
  children: ReactNode
}) {
  const lp = useLocalePath()
  const { code: country } = useCountry()
  if (!isAllowedInCountry("page:quiz", country)) return null

  const href = lp("/quiz-focus")
  const label = `Quiz from ${calculator} calculator`
  const location = `${calculator}-calculator-result`
  return (
    <Link
      href={href}
      data-testid={`${calculator}-quiz-cta`}
      data-tracking-label={label}
      data-tracking-location={location}
      onClick={() => track.ctaClick(label, location, href)}
      className={cn(buttonVariants({ variant: "primary" }), "group mt-4")}
    >
      {children}
      <ArrowRightIcon className="ml-2 transition-transform duration-200 ease-out group-hover:translate-x-1" />
    </Link>
  )
}
